import { Loader2, AlertCircle, Map as MapIcon } from "lucide-react";
import type { MapPreviewFrameProps } from "../../contracts/map-assets";
import { cn } from "../../lib/utils";

/**
 * A reusable embedded map shell for inspection flows.
 * Provides a consistent frame with header, optional status, toolbar, and viewport.
 */
export function MapPreviewFrame({
  title,
  description,
  helperText,
  status,
  loading,
  errorMessage,
  emptyMessage,
  toolbar,
  viewport,
  footer,
  className,
}: MapPreviewFrameProps) {
  return (
    <div
      className={cn(
        "flex flex-col h-full overflow-hidden bg-background border border-border rounded-lg shadow-sm",
        className
      )}
    >
      {/* Header */}
      <div className="flex flex-col gap-1 p-3 border-b border-border bg-muted/5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col min-w-0">
            <h3 className="text-sm font-black tracking-tight text-foreground truncate uppercase">
              {title}
            </h3>
            {description && (
              <p className="text-[11px] leading-tight text-muted-foreground truncate">
                {description}
              </p>
            )}
          </div>

          {status && (
            <div
              className={cn(
                "shrink-0 px-2 py-0.5 rounded-sm text-[10px] font-black uppercase tracking-wider border",
                status.tone === "success" && "bg-success/10 text-success border-success/20",
                status.tone === "warning" && "bg-warning/10 text-warning border-warning/20",
                status.tone === "danger" && "bg-destructive/10 text-destructive border-destructive/20",
                status.tone === "muted" && "bg-muted text-muted-foreground border-border",
                (!status.tone || status.tone === "default") && "bg-primary/10 text-primary border-primary/20"
              )}
            >
              {status.label}
            </div>
          )}
        </div>
      </div>

      {/* Toolbar */}
      {toolbar && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/10">
          {toolbar}
        </div>
      )}

      {/* Viewport Region */}
      <div className="relative flex-1 min-h-0 bg-muted/20 overflow-hidden">
        {viewport}

        {/* Loading Overlay */}
        {loading && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-background/60 backdrop-blur-[1px] animate-in fade-in duration-200">
            <Loader2 className="h-6 w-6 text-primary animate-spin" />
            <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">
              Loading Map...
            </span>
          </div>
        )}

        {/* Error Overlay */}
        {errorMessage && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 p-6 bg-background/90 text-center animate-in fade-in duration-200">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <AlertCircle className="h-6 w-6" />
            </div>
            <div className="space-y-1">
              <h4 className="text-xs font-black text-foreground uppercase tracking-tight">
                Preview Failed
              </h4>
              <p className="text-[11px] text-muted-foreground leading-relaxed max-w-[240px]">
                {errorMessage}
              </p>
            </div>
          </div>
        )}

        {/* Empty Overlay */}
        {!loading && !errorMessage && emptyMessage && (
          <div className="absolute inset-0 z-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted/40 text-muted-foreground/40">
              <MapIcon className="h-5 w-5" />
            </div>
            <p className="text-[11px] font-medium text-muted-foreground/60 max-w-[200px]">
              {emptyMessage}
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      {(footer || helperText) && (
        <div className="flex items-center justify-between gap-4 p-2 px-3 border-t border-border bg-muted/5">
          <div className="flex-1">
            {helperText && (
              <p className="text-[10px] font-medium text-muted-foreground/70 italic leading-tight">
                {helperText}
              </p>
            )}
          </div>
          {footer && <div className="shrink-0">{footer}</div>}
        </div>
      )}
    </div>
  );
}
